<template>
  <v-dialog
    v-model="opened"
    @keydown.esc="$emit('update:opened', false)"
    scrollable
    width="800"
    persistent
  >
    <v-card v-bind:id="variableUiParentElId()">
      <v-card-title class="title">
        Customize Questionnaire Messages
      </v-card-title>
      <p class="px-3" style="text-align: left; color: var(--gray-800)">
        These are the messages that will be emailed out, sent via SMS, or found
        in conversations.
      </p>
      <v-card-text class="text-xs-left pt-0">
        <v-alert :value="true" color="info" outline class="mb-2">
          <div id="questionnaire-customization-dialog-variables-helper-content">
            <p>
              <b>
                Please note these changes will only affect the newly sent
                questionnaires based on the template, not the ones that were
                sent already.
              </b>
            </p>

            <p>
              For email, in-app messages, and SMS notification, you can use
              customized words (variables). These variables will then be
              replaced with the actual values when the message is sent. For
              example, "{questionnaire_year}" will be replaced with "2024" if
              the questionnaire's year is 2024.
            </p>

            <p class="mb-0">
              All of the available customized words (variables) can be found
              <a
                href="https://helpcenter.soraban.com/customized-words-variables-new"
                style="cursor: pointer"
                target="_blank"
              >
                here (opens in a new tab).
              </a>
            </p>
          </div>
        </v-alert>

        <v-tabs
          v-model="activeTab"
          color="white"
          slider-color="primary"
          class="mb-2"
        >
          <v-tab key="initial" rippled class="text-none subheading">
            <v-layout align-center>
              <span>Initial</span>
            </v-layout>
          </v-tab>
          <v-tab key="reminder" ripple class="text-none subheading">
            <v-layout align-center>
              <span>Reminder</span>
            </v-layout>
          </v-tab>
          <v-tab key="completion" ripple class="text-none subheading">
            <v-layout align-center>
              <span>Completion</span>
            </v-layout>
          </v-tab>
          <v-tab key="other" ripple class="text-none subheading">
            <v-layout align-center>
              <span>Other</span>
            </v-layout>
          </v-tab>
        </v-tabs>
        <v-layout v-if="activeTab === 0" column>
          <span class="font-weight-bold"
            >Initial Email
            <v-tooltip bottom>
              <v-icon size="15" class="ml-1" slot="activator"> info </v-icon>
              <span
                >This will be sent with a button that will take the client to
                the questionnaire at the bottom: "Open Questionnaire"</span
              >
            </v-tooltip>
          </span>
          <span>Subject</span>
          <v-text-field
            v-model="initialEmailSubject"
            :data-model-name="'initialEmailSubject'"
            class="default-input"
            :placeholder="initialEmailDefaultSubject"
            solo
          />
          <span>Body</span>
          <v-textarea
            v-model="initialEmailMessage"
            :data-model-name="'initialEmailMessage'"
            class="default-input"
            :placeholder="initialEmailDefaultMessage"
            solo
            rows="10"
          />
          <span class="font-weight-bold"
            >Initial Soraban Message
            <v-tooltip bottom>
              <v-icon size="15" class="ml-1" slot="activator"> info </v-icon>
              <span
                >This will be sent with a link for the questionnaire at the
                bottom: "Click <u>here</u> to view it."</span
              >
            </v-tooltip>
          </span>
          <v-textarea
            v-model="initialInAppMessage"
            :data-model-name="'initialInAppMessage'"
            class="default-input"
            :placeholder="initialInAppDefaultMessage"
            solo
          />
          <span class="font-weight-bold"
            >Initial SMS Notification
            <v-tooltip bottom>
              <v-icon size="15" class="ml-1" slot="activator"> info </v-icon>
              <span
                >The message will be sent if the client has a phone number and
                has opted in to receive SMS notification.</span
              >
            </v-tooltip>
          </span>
          <v-textarea
            v-model="initialSmsMessage"
            :data-model-name="'initialSmsMessage'"
            class="default-input"
            :placeholder="reminderSmsDefaultMessage"
            solo
          />
        </v-layout>

        <v-layout v-else-if="activeTab === 1" column>
          <span class="font-weight-bold"
            >Reminder Email
            <v-tooltip bottom>
              <v-icon size="15" class="ml-1" slot="activator"> info </v-icon>
              <span
                >This will be sent with a button that will take the client to
                the questionnaire at the bottom: "Open Questionnaire"</span
              >
            </v-tooltip>
          </span>
          <span>Subject</span>
          <v-text-field
            v-model="reminderEmailSubject"
            :data-model-name="'reminderEmailSubject'"
            class="default-input"
            :placeholder="reminderEmailDefaultSubject"
            solo
          />
          <span>Body</span>
          <v-textarea
            v-model="reminderEmailMessage"
            :data-model-name="'reminderEmailMessage'"
            class="default-input"
            :placeholder="reminderEmailDefaultMessage"
            solo
            rows="10"
          />

          <span class="font-weight-bold"
            >Reminder In-app Message
            <v-tooltip bottom>
              <v-icon size="15" class="ml-1" slot="activator"> info </v-icon>
              <span
                >This will be sent with a link for the questionnaire at the
                bottom: "Click <u>here</u> to view it."</span
              >
            </v-tooltip>
          </span>
          <v-textarea
            v-model="reminderInAppMessage"
            :data-model-name="'reminderInAppMessage'"
            class="default-input"
            :placeholder="reminderInAppDefaultMessage"
            solo
          />

          <span class="font-weight-bold"
            >Reminder SMS Message
            <v-tooltip bottom>
              <v-icon size="15" class="ml-1" slot="activator"> info </v-icon>
              <span
                >The message will be sent if the client has a phone number and
                has opted in to receive SMS notification.</span
              >
            </v-tooltip>
          </span>
          <v-textarea
            v-model="reminderSmsMessage"
            :data-model-name="'reminderSmsMessage'"
            class="default-input"
            :placeholder="reminderSmsDefaultMessage"
            solo
          />
        </v-layout>

        <v-layout column v-else-if="activeTab === 2">
          <span class="font-weight-bold"
            >Questionnaire Completion Email
            <v-tooltip bottom>
              <v-icon size="15" class="ml-1" slot="activator"> info </v-icon>
              <span
                >This will be sent in an email when the questionnaire is
                completed.</span
              >
            </v-tooltip>
          </span>

          <span>Subject</span>
          <v-text-field
            v-model="questionnaireCompletionSubject"
            :data-model-name="'questionnaireCompletionSubject'"
            class="default-input"
            placeholder="Leave blank to disable"
            solo
          />
          <span>Body</span>
          <v-textarea
            v-model="questionnaireCompletionMessage"
            :data-model-name="'questionnaireCompletionMessage'"
            class="default-input"
            placeholder="Leave blank to disable"
            solo
            rows="8"
          />
        </v-layout>

        <v-layout column v-else>
          <span class="font-weight-bold"
            >Disclaimer
            <v-tooltip bottom>
              <v-icon size="15" class="ml-1" slot="activator"> info </v-icon>
              <span
                >This will be asked at the end of the questionnaire with an "I
                agree" check box. Blank one will hide "I agree"
                requirement.</span
              >
            </v-tooltip>
          </span>
          <v-textarea
            v-model="disclaimer"
            :data-model-name="'disclaimer'"
            class="default-input"
            :placeholder="defaultDisclaimer"
            solo
            rows="6"
          />
        </v-layout>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn class="primary text-capitalize" @click="saveTemplate()">
          save
        </v-btn>
        <v-btn
          class="error--text text-capitalize"
          outline
          @click="$emit('update:opened', false)"
        >
          cancel
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import isEmpty from 'lodash/isEmpty';
import API from '@/api';
import {
  questionnaireVariables,
  entityVariables,
  userVariables,
} from '@/constants/variables';

export default {
  props: {
    opened: {
      type: Boolean,
    },

    closed: {
      type: Boolean,
    },

    selectedQuestionnaire: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      questionnaireVariables,
      entityVariables,
      userVariables,
      activeTab: 1,
      initialEmailSubject: '',
      initialEmailMessage: '',
      initialInAppMessage: '',
      initialReminderSmsMessage: '',
      reminderEmailSubject: '',
      reminderEmailMessage: '',
      reminderInAppMessage: '',
      reminderSmsMessage: '',
      disclaimer: '',
      initialEmailDefaultSubject:
        'Please complete {questionnaire_year} {questionnaire_name}{for_wording}',
      initialEmailDefaultMessage:
        'Hi {client_fname}!\n\n{firm_name} is inviting you to complete the {questionnaire_year} {questionnaire_name}{for_wording}. Please click the button below to view & sign the engagement letter and fill out the questionnaire{by_due_date}\n\nIf you have any problems with the website, please email us at hello@soraban.com\n\nFor all other questions, please send a message using the message icon on the top right.',
      initialInAppDefaultMessage:
        'This is a reminder to complete the {questionnaire_year} {questionnaire_name}{for_wording}{by_due_date}.',
      initialSmsMessage:
        '{firm_name}: Hi {client_fname}, please complete the {questionnaire_year} {questionnaire_name}{for_wording}{by_due_date}.',
      reminderEmailDefaultSubject:
        'Reminder to complete {questionnaire_year} {questionnaire_name}{for_wording}',
      reminderEmailDefaultMessage:
        'Hi {client_fname}!\n\nThis is a reminder to finish the {questionnaire_year} {questionnaire_name}{for_wording}. You still have {number_of_items_left} questions that need to be answered. Please click the button below to begin.\n\nIf you have any problems with the website, please email us at hello@soraban.com\n\nFor all other questions, please send a message using the message icon on the top right.',
      reminderInAppDefaultMessage:
        'This is a reminder to complete the {questionnaire_year} {questionnaire_name}{for_wording}{by_due_date}.',
      reminderSmsDefaultMessage:
        '{firm_name}: Hi {client_fname}, this is a reminder to complete the {questionnaire_year} {questionnaire_name}{for_wording}{by_due_date}.',
      defaultDisclaimer:
        '•You confirm that you have accurately answered all questions to the best of your knowledge and have uploaded most, if not all, requested documents that are available to you.\n\n•Please be aware that we may not be able to begin our work until you provide all the necessary information.',
      questionnaireCompletionSubject: '',
      questionnaireCompletionMessage: '',
    };
  },

  methods: {
    isEmpty,
    onlyUnique(value, index, self) {
      return self.indexOf(value) === index;
    },
    reset() {
      this.initialEmailSubject = this.selectedQuestionnaire.initial_email_subject;
      this.initialEmailMessage = this.selectedQuestionnaire.initial_email_message;
      this.initialInAppMessage = this.selectedQuestionnaire.initial_in_app_message;
      this.initialSmsMessage = this.selectedQuestionnaire.initial_sms_message;

      this.reminderEmailSubject = this.selectedQuestionnaire.reminder_email_subject;
      this.reminderEmailMessage = this.selectedQuestionnaire.reminder_email_message;
      this.reminderInAppMessage = this.selectedQuestionnaire.reminder_in_app_message;
      this.reminderSmsMessage = this.selectedQuestionnaire.reminder_sms_message;

      this.questionnaireCompletionSubject = this.selectedQuestionnaire.questionnaire_completion_subject;
      this.questionnaireCompletionMessage = this.selectedQuestionnaire.questionnaire_completion_message;

      this.disclaimer = this.selectedQuestionnaire.disclaimer;
      this.activeTab = 0;
    },
    getAllowedVariables() {
      return new Set([
        ...userVariables.match(/{[^}]+}/g),
        ...entityVariables.match(/{[^}]+}/g),
        ...questionnaireVariables.match(/{[^}]+}/g),
        '{for_wording}',
      ]);
    },
    async saveTemplate() {
      try {
        const allowedVariables = this.getAllowedVariables();

        function findDisallowedVariables(compiledString) {
          // Extract all placeholders in the format {some_variable}
          const foundVariables = compiledString.match(/{[^}]+}/g) || [];

          // Find any variables that are not in the allowedVariables set
          const disallowedVariables = foundVariables.filter(
            variable => !allowedVariables.has(variable),
          );

          // Return only unique values using Set
          return [...new Set(disallowedVariables)];
        }

        const compiledTogether =
          this.initialEmailSubject +
          this.initialEmailMessage +
          this.initialInAppMessage +
          this.initialSmsMessage +
          this.reminderEmailSubject +
          this.reminderEmailMessage +
          this.reminderInAppMessage +
          this.reminderSmsMessage +
          this.disclaimer +
          this.questionnaireCompletionSubject +
          this.questionnaireCompletionMessage;

        const disallowed = findDisallowedVariables(compiledTogether);

        if (disallowed && disallowed.length > 0) {
          return this.$store.dispatch('application/setErrorNotification', {
            message: `The following customized words (variables) are not allowed: ${disallowed.join(
              ', ',
            )}`,
          });
        }

        const response = await API.questionnaireTemplate.updateQuestionnaireTemplate(
          {
            id: this.selectedQuestionnaire.id,
            initial_email_subject: this.initialEmailSubject,
            initial_email_message: this.initialEmailMessage,
            initial_in_app_message: this.initialInAppMessage,
            initial_sms_message: this.initialSmsMessage,
            reminder_email_subject: this.reminderEmailSubject,
            reminder_email_message: this.reminderEmailMessage,
            reminder_in_app_message: this.reminderInAppMessage,
            reminder_sms_message: this.reminderSmsMessage,
            disclaimer: this.disclaimer,
            questionnaire_completion_subject: this
              .questionnaireCompletionSubject,
            questionnaire_completion_message: this
              .questionnaireCompletionMessage,
          },
        );
        if (response.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
          });
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message: 'You have saved the template.',
          });
          this.$emit('refresh-questionnaires');
          this.$emit('update:opened', false);
        }
      } catch (e) {
        console.log(e);
      }
    },
    variableUiParentElId(tab) {
      return (
        'questionnaire-customization-dialog' +
        this.selectedQuestionnaire.id +
        `-${tab ? tab : this.activeTab}`
      );
    },
    setUpVariableUI(id, newTabValue) {
      const currentTab = newTabValue;

      const waitForParentEl = (id, callback) => {
        let elapsedTime = 0;
        const interval = 100; // Check every 100ms
        const maxTime = 30000;

        const waitForElInterval = setInterval(() => {
          const parentEl = document.getElementById(this.variableUiParentElId());
          elapsedTime += interval;

          if (parentEl) {
            clearInterval(waitForElInterval); // Stop checking once the element is found
            callback(parentEl); // Run the rest of the logic
          } else if (elapsedTime >= maxTime) {
            clearInterval(waitForElInterval);
            console.log('Element not found within 30 seconds');
          }
        }, interval);
      };

      waitForParentEl(id, parentEl => {
        const addClickListener = (el, listener) => {
          if (!el._clickListeners) el._clickListeners = [];
          el._clickListeners.push(listener);
          el.addEventListener('click', listener);
        };

        const removeClickListeners = el => {
          if (el._clickListeners) {
            el._clickListeners.forEach(listener => {
              el.removeEventListener('click', listener);
            });
            el._clickListeners = [];
          }
        };

        const addBlurListener = (el, listener) => {
          if (!el._blurListeners) el._blurListeners = [];
          el._blurListeners.push(listener);
          el.addEventListener('blur', listener);
        };

        const removeBlurListeners = el => {
          if (el._blurListeners) {
            el._blurListeners.forEach(listener => {
              el.removeEventListener('blur', listener);
            });
            el._blurListeners = [];
          }
        };

        document.querySelectorAll('.prototype-tagging').forEach(tagging => {
          const grandparent = tagging.parentElement.parentElement;

          if (grandparent.id && grandparent.id.includes(parentEl.id)) {
            removeClickListeners(grandparent);
            const input = tagging.parentElement.querySelector(
              'input[type="text"], textarea',
            );
            removeBlurListeners(input);
          }

          tagging.remove();
        });

        const handleEls = input => {
          const taggingDiv = document.createElement('div');

          taggingDiv.className = 'prototype-tagging';
          taggingDiv.style.height = '100%';
          taggingDiv.style.overflow = 'scroll';
          taggingDiv.style.lineHeight = '1.6';
          taggingDiv.style.paddingBlock = '8px';
          taggingDiv.innerHTML = input.value;

          if (window.innerWidth < 740) {
            taggingDiv.style.width = '85vw';
          } else {
            taggingDiv.style.width = '740px';
          }

          input.insertAdjacentElement('afterend', taggingDiv);

          if (input.value) {
            input.style.display = 'none';
            taggingDiv.style.display = 'block';
          } else {
            input.style.display = 'block';
            taggingDiv.style.display = 'none';
          }
        };

        const allowedVariables = [...this.getAllowedVariables()].map(str =>
          str.replace('{', '').replace('}', ''),
        );

        const updateContenteditable = (contentOverride, el) => {
          let content = contentOverride || el.innerHTML;

          content = content
            .replace(/ /g, '&nbsp;')
            .replace(/\t/g, '&nbsp;&nbsp;')
            .replace(/\n/g, '<br>')
            .replace(/<\/span>/g, '}')
            .replace(
              /<span style="display: inline-block;padding: 0px 6px;margin-bottom: 2px;margin-right: 2px;background-color: var(--blue-100);box-shadow: inset 0 0 0 1px var(--blue-200);border-radius: 4px;font-weight: 500;line-height: 1.6;">/g,
              '{',
            );

          allowedVariables.forEach(variable => {
            const escapedVariable = variable
              .replace(/[-[\]{}()*+?.,\\^$|#]/g, '\\$&')
              .replace(/ /g, '(?: |&nbsp;)');

            const regex = new RegExp(`\\{${escapedVariable}\\}`, 'g');

            content = content.replace(
              regex,
              `<span style="display: inline-block;padding: 0px 6px;margin-bottom: 2px;margin-right: 2px;background-color: var(--blue-100);box-shadow: inset 0 0 0 1px var(--blue-200);border-radius: 4px;font-weight: 500;line-height: 1.6;">${variable}</span>`,
            );
          });

          content = content;

          el.innerHTML = content;
          el.focus();
        };

        const handleInput = input => {
          removeBlurListeners(input);

          const eventListener = () => {
            const el = input.parentElement.querySelector('div');
            updateContenteditable(input.value, el);

            if (input.value) {
              el.style.display = 'block';
              input.style.display = 'none';
            }
          };

          addBlurListener(input, eventListener);
        };

        parentEl.querySelectorAll('input[type="text"]').forEach(input => {
          handleEls(input);
          handleInput(input);
        });

        parentEl.querySelectorAll('textarea').forEach(input => {
          handleEls(input);
          handleInput(input);
        });

        parentEl.querySelectorAll('.prototype-tagging').forEach(tagging => {
          const grandparent = tagging.parentElement.parentElement;
          const input = tagging.parentElement.querySelector(
            'input[type="text"], textarea',
          );
          const modelName = input && input.getAttribute('data-model-name');
          const id = `${parentEl.id}---${currentTab}-${modelName}`;
          grandparent.id = id;

          removeClickListeners(grandparent);

          const eventListener = () => {
            const input = tagging.parentElement.querySelector(
              'input[type="text"], textarea',
            );

            if (input) {
              handleInput(input);
              input.style.display = 'block';
              tagging.style.display = 'none';
              input.focus();
            }
          };

          addClickListener(grandparent, eventListener);
          updateContenteditable(null, tagging);
        });
      });
    },
  },
  watch: {
    opened(newVal) {
      if (newVal) {
        this.reset();
        this.setUpVariableUI(this.selectedQuestionnaire.id, 0);
      }
    },
    activeTab(newVal) {
      if (newVal || newVal === 0) {
        this.setUpVariableUI(this.selectedQuestionnaire.id, newVal);
      }
    },
  },
};
</script>
